import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { ArrowLeft, ShoppingCart, Tag, Search, Minus, Plus, Trash2, CheckCircle2, Sparkles, Store, Repeat } from 'lucide-react';
import { timeAgo } from '../utils/timeAgo';
import SellCardDialog from '../components/SellCardDialog';
import MyShopDialog from '../components/MyShopDialog';

const fmt = (n) => `${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })} XRP`;
const fmtC = (n, c) => `${Number(n).toLocaleString(undefined, { maximumFractionDigits: c === 'FUZZY' ? 0 : 2 })} ${c}`;
const CART_KEY = 'shop_cart';

const loadCart = () => {
  try { return JSON.parse(localStorage.getItem(CART_KEY)) || {}; } catch { return {}; }
};

export default function Shop() {
  const navigate = useNavigate();
  const { user, isAuthenticated, updateBalances } = useAuth();
  const [listings, setListings] = useState([]);
  const [recentlySold, setRecentlySold] = useState([]);
  const [config, setConfig] = useState({ shipping_domestic: 30, shipping_international: 70, discount_tiers: [] });
  const [cart, setCart] = useState(loadCart);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState('newest');
  const [selected, setSelected] = useState(null);
  const [dialogQty, setDialogQty] = useState(1);
  const [showCart, setShowCart] = useState(false);
  const [shippingZone, setShippingZone] = useState('domestic');
  const [busy, setBusy] = useState(false);
  const [showSell, setShowSell] = useState(false);
  const [showMyShop, setShowMyShop] = useState(false);
  const [buyCurrency, setBuyCurrency] = useState('XRP');
  const [offerAmount, setOfferAmount] = useState('');

  const fetchAll = useCallback(() => {
    axios.get(`${API}/shop/listings`).then(r => {
      setListings(r.data.listings || []);
      setRecentlySold(r.data.recently_sold || []);
    }).catch(() => {});
    axios.get(`${API}/shop/config`).then(r => setConfig(r.data)).catch(() => {});
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { localStorage.setItem(CART_KEY, JSON.stringify(cart)); }, [cart]);

  const byId = useMemo(() => Object.fromEntries(listings.map(l => [l.listing_id, l])), [listings]);

  // Drop cart entries for cards that sold out / were removed
  useEffect(() => {
    if (listings.length === 0) return;
    setCart(c => {
      const next = {};
      for (const [id, qty] of Object.entries(c)) {
        const l = byId[id];
        if (l) next[id] = Math.min(qty, l.quantity || 1);
      }
      return Object.keys(next).length === Object.keys(c).length &&
        Object.entries(next).every(([k, v]) => c[k] === v) ? c : next;
    });
  }, [listings, byId]);

  const filtered = useMemo(() => {
    let out = listings;
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      out = out.filter(l => `${l.title} ${l.grade} ${l.condition}`.toLowerCase().includes(q));
    }
    if (filter === 'limited') out = out.filter(l => l.is_limited);
    if (filter === 'regular') out = out.filter(l => !l.is_limited);
    if (sort === 'price_asc') out = [...out].sort((a, b) => a.price_xrp - b.price_xrp);
    else if (sort === 'price_desc') out = [...out].sort((a, b) => b.price_xrp - a.price_xrp);
    return out;
  }, [listings, query, filter, sort]);

  const cartEntries = Object.entries(cart).filter(([id]) => byId[id]);
  const cartCount = cartEntries.reduce((s, [, q]) => s + q, 0);
  const regularQty = cartEntries.reduce((s, [id, q]) => s + (byId[id].is_limited ? 0 : q), 0);
  const regularSubtotal = cartEntries.reduce((s, [id, q]) => s + (byId[id].is_limited ? 0 : byId[id].price_xrp * q), 0);
  const limitedSubtotal = cartEntries.reduce((s, [id, q]) => s + (byId[id].is_limited ? byId[id].price_xrp * q : 0), 0);
  const tiers = [...(config.discount_tiers || [])].sort((a, b) => a.min_qty - b.min_qty);
  const discountPct = tiers.reduce((p, t) => (regularQty >= t.min_qty ? Math.max(p, t.pct) : p), 0);
  const discount = regularSubtotal * discountPct / 100;
  const nextTier = tiers.find(t => regularQty < t.min_qty);
  const shippingFee = shippingZone === 'international' ? config.shipping_international : config.shipping_domestic;
  const total = regularSubtotal + limitedSubtotal - discount + shippingFee;

  const addToCart = (l, qty) => {
    setCart(c => ({ ...c, [l.listing_id]: Math.min((c[l.listing_id] || 0) + qty, l.quantity || 1) }));
    toast.success(`${l.title} added to cart`);
    setSelected(null);
  };
  const setQty = (id, qty) => {
    setCart(c => {
      if (qty <= 0) { const { [id]: _, ...rest } = c; return rest; }
      return { ...c, [id]: Math.min(qty, byId[id]?.quantity || 1) };
    });
  };

  const checkout = async () => {
    if (!isAuthenticated) { toast.error('Log in to check out'); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/shop/cart/checkout`, {
        items: cartEntries.map(([id, qty]) => ({ listing_id: id, qty })),
        shipping_zone: shippingZone,
      });
      updateBalances({ balance: res.data.new_balance });
      setCart({});
      setShowCart(false);
      toast.success(`Order placed — ${res.data.order.item_count} cards! Submit your shipping address in My Games.`);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Checkout failed');
      fetchAll();
    } finally { setBusy(false); }
  };

  const buyNow = async () => {
    if (!isAuthenticated) { toast.error('Log in to buy'); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/shop/listings/${selected.listing_id}/buy`, { currency: buyCurrency });
      updateBalances({ balance: res.data.new_balance, fuzzy_balance: res.data.new_fuzzy_balance });
      toast.success(res.data.message + ' — submit your shipping address in My Games.');
      setSelected(null);
      fetchAll();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Purchase failed'); }
    finally { setBusy(false); }
  };

  const sendOffer = async () => {
    if (!isAuthenticated) { toast.error('Log in to make an offer'); return; }
    const amt = parseFloat(offerAmount);
    if (!amt || amt <= 0) { toast.error('Enter an offer amount'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/shop/listings/${selected.listing_id}/offer`, { amount: amt, currency: buyCurrency });
      toast.success('Offer sent! The seller will accept or decline.');
      setOfferAmount('');
      setSelected(null);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Offer failed'); }
    finally { setBusy(false); }
  };

  const chip = (key, label) => (
    <button
      key={key}
      data-testid={`shop-filter-${key}`}
      onClick={() => setFilter(key)}
      className={`text-[11px] font-semibold px-3 py-1.5 rounded-full border transition-colors ${filter === key ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-300' : 'bg-slate-800/60 border-slate-700 text-slate-400'}`}
    >{label}</button>
  );

  return (
    <div className="min-h-screen bg-[#0a0e27] pb-28">
      <div className="sticky top-0 z-20 bg-[#0a0e27]/95 backdrop-blur border-b border-slate-800 px-4 py-3 flex items-center gap-3">
        <button data-testid="shop-back-button" onClick={() => navigate('/')} className="text-slate-400 hover:text-white">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-lg font-bold text-white leading-tight">Card Shop</h1>
          <p className="text-[10px] text-slate-500">{listings.length} cards · escrow-protected · ships signature-required</p>
        </div>
        <button data-testid="shop-cart-button" onClick={() => setShowCart(true)} className="relative p-2 rounded-xl bg-cyan-500/15 border border-cyan-500/30">
          <ShoppingCart className="w-5 h-5 text-cyan-300" />
          {cartCount > 0 && (
            <span data-testid="shop-cart-count" className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">{cartCount}</span>
          )}
        </button>
      </div>

      {isAuthenticated && (
        <div className="px-4 pt-3 grid grid-cols-2 gap-2">
          <button data-testid="shop-sell-button" onClick={() => setShowSell(true)} className="rounded-xl bg-purple-500/15 border border-purple-500/40 py-2.5 text-xs font-bold text-purple-300 flex items-center justify-center gap-1.5 active:scale-[0.98] transition-transform">
            <Tag className="w-3.5 h-3.5" /> Sell a card
          </button>
          <button data-testid="shop-my-shop-button" onClick={() => setShowMyShop(true)} className="rounded-xl bg-slate-800/60 border border-slate-700 py-2.5 text-xs font-bold text-slate-300 flex items-center justify-center gap-1.5 active:scale-[0.98] transition-transform">
            <Store className="w-3.5 h-3.5" /> My Shop
          </button>
        </div>
      )}

      <div className="px-4 pt-3 space-y-2.5">
        <div className="relative">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            data-testid="shop-search-input"
            placeholder="Search cards, grades…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            className="bg-slate-800/60 border-slate-700 text-white h-10 text-sm pl-9"
          />
        </div>
        <div className="flex items-center gap-1.5 overflow-x-auto pb-0.5">
          {chip('all', 'All')}
          {chip('regular', 'Bulk-eligible')}
          {chip('limited', '✨ Limited')}
          <div className="ml-auto flex-shrink-0">
            <select
              data-testid="shop-sort-select"
              value={sort}
              onChange={e => setSort(e.target.value)}
              className="text-[11px] bg-slate-800/60 border border-slate-700 text-slate-300 rounded-full px-2.5 py-1.5 outline-none"
            >
              <option value="newest">Newest</option>
              <option value="price_asc">Price ↑</option>
              <option value="price_desc">Price ↓</option>
            </select>
          </div>
        </div>
        {tiers.length > 0 && (
          <p className="text-[11px] text-amber-300/90 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
            🔥 Bulk deal: {tiers.map(t => `${t.min_qty}+ cards → ${t.pct}% off`).join(' · ')} <span className="text-slate-500">(limited cards excluded)</span>
          </p>
        )}
      </div>

      <div className="px-4 pt-3 grid grid-cols-2 gap-2.5">
        {filtered.map(l => {
          const inCart = cart[l.listing_id] || 0;
          return (
            <div
              key={l.listing_id}
              data-testid={`shop-grid-card-${l.listing_id.slice(0, 8)}`}
              onClick={() => { setSelected(l); setDialogQty(1); setOfferAmount(''); setBuyCurrency((l.payment_mode || 'xrp') === 'fuzzy' ? 'FUZZY' : 'XRP'); }}
              className={`relative rounded-2xl border cursor-pointer active:scale-[0.97] transition-all overflow-hidden ${l.is_limited ? 'bg-gradient-to-b from-amber-900/20 to-slate-800/60 border-amber-500/30' : 'bg-slate-800/50 border-slate-700/60'}`}
            >
              {l.photo_urls?.[0] ? (
                <img src={l.photo_urls[0]} alt={l.title} className="w-full aspect-square object-cover" />
              ) : (
                <div className="w-full aspect-square bg-slate-800/80 flex items-center justify-center">
                  <Tag className="w-8 h-8 text-slate-600" />
                </div>
              )}
              {l.is_limited && (
                <span className="absolute top-2 left-2 text-[9px] font-extrabold tracking-wider px-2 py-0.5 rounded-md bg-amber-500/90 text-slate-900 flex items-center gap-0.5">
                  <Sparkles className="w-2.5 h-2.5" /> LIMITED
                </span>
              )}
              {inCart > 0 && (
                <span className="absolute top-2 right-2 text-[9px] font-bold px-1.5 py-0.5 rounded-md bg-cyan-500 text-slate-900">{inCart} in cart</span>
              )}
              <div className="p-2.5">
                <p className="font-bold text-white text-[12px] leading-tight line-clamp-2">{l.title}</p>
                {l.seller_username ? (
                  <p className="text-[9px] text-purple-300 mt-0.5 truncate">Community · @{l.seller_username}</p>
                ) : (l.grade || l.condition) ? (
                  <p className="text-[9px] text-slate-500 mt-0.5 truncate">{[l.grade, l.condition].filter(Boolean).join(' · ')}</p>
                ) : null}
                <div className="flex items-center justify-between mt-1.5">
                  <span className="text-cyan-300 font-bold text-[12px] flex items-center gap-1">
                    {(l.payment_mode || 'xrp') === 'fuzzy' ? fmtC(l.price_fuzzy, 'FUZZY') : fmt(l.price_xrp)}
                    {l.payment_mode === 'both' && <Repeat className="w-3 h-3 text-amber-400" />}
                  </span>
                  <span className={`text-[9px] font-semibold ${(l.quantity || 1) <= 2 ? 'text-red-400' : 'text-slate-500'}`}>{l.quantity || 1} left</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {filtered.length === 0 && (
        <p className="text-center text-sm text-slate-500 py-10">No cards match your search</p>
      )}

      {recentlySold.length > 0 && (
        <div className="px-4 pt-5 space-y-2">
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">Recently sold</p>
          {recentlySold.map(l => (
            <div key={l.listing_id} data-testid={`shop-sold-${l.listing_id.slice(0, 8)}`} className="rounded-xl bg-slate-800/40 border border-slate-700/50 px-3 py-2 flex items-center gap-2">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
              <p className="text-[11px] text-slate-400 truncate flex-1">
                <span className="text-slate-300 font-semibold">{l.title}</span> · sold for <span className="text-emerald-300 font-bold">{fmt(l.sold_price)}</span>
                {l.sold_at && <span className="text-slate-600"> · {timeAgo(l.sold_at)}</span>}
              </p>
              <span data-testid="shop-sold-badge" className="text-[9px] font-extrabold px-1.5 py-0.5 rounded bg-red-500/15 text-red-400 border border-red-500/40 rotate-[-6deg] flex-shrink-0">SOLD</span>
            </div>
          ))}
        </div>
      )}

      {cartCount > 0 && !showCart && (
        <button
          data-testid="shop-cart-bar"
          onClick={() => setShowCart(true)}
          className="fixed bottom-4 left-4 right-4 z-30 rounded-2xl bg-gradient-to-r from-cyan-600 to-blue-600 px-4 py-3.5 flex items-center justify-between shadow-lg shadow-cyan-900/40 active:scale-[0.98] transition-transform"
        >
          <span className="text-white font-bold text-sm flex items-center gap-2">
            <ShoppingCart className="w-4 h-4" /> {cartCount} card{cartCount > 1 ? 's' : ''}
            {discountPct > 0 && <span className="text-[10px] bg-white/20 rounded-full px-2 py-0.5">{discountPct}% off</span>}
          </span>
          <span className="text-white font-bold text-sm">View cart · {fmt(total)}</span>
        </button>
      )}

      {/* Card detail dialog */}
      <Dialog open={!!selected} onOpenChange={v => { if (!v) setSelected(null); }}>
        <DialogContent data-testid="shop-detail-dialog" className="bg-slate-900 border-slate-700 w-[calc(100%-2rem)] max-w-sm max-h-[85vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="text-lg text-white flex items-center gap-2">
                  {selected.title}
                  {selected.is_limited && <span className="text-[9px] font-extrabold px-1.5 py-0.5 rounded bg-amber-500/90 text-slate-900">LIMITED</span>}
                </DialogTitle>
                <DialogDescription className={selected.description ? 'text-slate-400 text-xs' : 'sr-only'}>
                  {selected.description || 'Card Shop listing'}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                {selected.photo_urls?.length > 0 && (
                  <div className="flex gap-2 overflow-x-auto">
                    {selected.photo_urls.map((u, i) => (
                      <img key={i} src={u} alt="" className="h-32 rounded-lg border border-slate-700 object-cover" />
                    ))}
                  </div>
                )}
                <div className="flex flex-wrap gap-1.5">
                  {selected.grade && <span className="text-[10px] px-2 py-0.5 rounded-md bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">{selected.grade}</span>}
                  {selected.condition && <span className="text-[10px] px-2 py-0.5 rounded-md bg-slate-700/50 text-slate-300 border border-slate-600">{selected.condition}</span>}
                  {selected.cert_number && <span className="text-[10px] px-2 py-0.5 rounded-md bg-indigo-500/10 text-indigo-300 border border-indigo-500/30">Cert #{selected.cert_number}</span>}
                  <span className="text-[10px] px-2 py-0.5 rounded-md bg-cyan-500/10 text-cyan-300 border border-cyan-500/30">{selected.quantity || 1} in stock</span>
                </div>
                {selected.is_limited && (
                  <p className="text-[10px] text-amber-300/90 bg-amber-500/10 border border-amber-500/20 rounded-lg px-2.5 py-1.5">
                    ✨ Limited card — priced as marked, excluded from the bulk-quantity discount.
                  </p>
                )}
                {selected.seller_username ? (
                  <>
                    <p className="text-[10px] text-purple-300 bg-purple-500/10 border border-purple-500/20 rounded-lg px-2.5 py-1.5">
                      🛍 Community card sold by <b>@{selected.seller_username}</b> — bought individually (not part of the bulk cart). Escrow-protected.
                    </p>
                    {(selected.payment_mode || 'xrp') === 'both' && (
                      <div className="grid grid-cols-2 gap-2">
                        {['XRP', 'FUZZY'].map(c => (
                          <button key={c} data-testid={`buy-currency-${c.toLowerCase()}`} onClick={() => setBuyCurrency(c)}
                            className={`rounded-xl border p-2.5 text-center transition-colors ${buyCurrency === c ? 'bg-amber-500/15 border-amber-500/50' : 'bg-slate-800/50 border-slate-700'}`}>
                            <p className={`text-xs font-bold ${buyCurrency === c ? 'text-amber-300' : 'text-white'}`}>{c === 'XRP' ? fmt(selected.price_xrp) : fmtC(selected.price_fuzzy, 'FUZZY')}</p>
                            <p className="text-[9px] text-slate-500">pay in {c}</p>
                          </button>
                        ))}
                      </div>
                    )}
                    <Button
                      data-testid="shop-buy-now"
                      onClick={buyNow}
                      disabled={busy}
                      className="w-full bg-gradient-to-r from-emerald-500 to-cyan-600 hover:from-emerald-600 hover:to-cyan-700 h-11 font-semibold"
                    >
                      {busy ? 'Buying…' : `Buy It Now — ${buyCurrency === 'FUZZY' ? fmtC(selected.price_fuzzy, 'FUZZY') : fmt(selected.price_xrp)}`}
                    </Button>
                    {selected.allow_offers !== false && (selected.quantity || 1) === 1 && (
                      <div className="flex gap-2">
                        <Input
                          data-testid="shop-offer-amount"
                          type="number"
                          placeholder={`Your offer (${buyCurrency})`}
                          value={offerAmount}
                          onChange={e => setOfferAmount(e.target.value)}
                          className="bg-slate-800 border-slate-700 text-white text-sm h-10 flex-1"
                        />
                        <Button data-testid="shop-offer-send" onClick={sendOffer} disabled={busy} variant="outline" className="border-amber-500/40 text-amber-300 h-10">
                          Make Offer
                        </Button>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="flex items-center justify-between bg-slate-800/50 rounded-xl p-3">
                      <span className="text-white font-bold">{fmt(selected.price_xrp)} <span className="text-slate-500 text-xs font-normal">each</span></span>
                      <div className="flex items-center gap-2">
                        <button data-testid="shop-qty-minus" onClick={() => setDialogQty(q => Math.max(1, q - 1))} className="w-8 h-8 rounded-lg bg-slate-700 flex items-center justify-center text-white disabled:opacity-40" disabled={dialogQty <= 1}>
                          <Minus className="w-3.5 h-3.5" />
                        </button>
                        <span data-testid="shop-qty-value" className="text-white font-bold w-6 text-center">{dialogQty}</span>
                        <button data-testid="shop-qty-plus" onClick={() => setDialogQty(q => Math.min(selected.quantity || 1, q + 1))} className="w-8 h-8 rounded-lg bg-slate-700 flex items-center justify-center text-white disabled:opacity-40" disabled={dialogQty >= (selected.quantity || 1)}>
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                    <Button
                      data-testid="shop-add-to-cart"
                      onClick={() => addToCart(selected, dialogQty)}
                      className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 h-11 font-semibold"
                    >
                      Add {dialogQty > 1 ? `${dialogQty} ` : ''}to Cart — {fmt(selected.price_xrp * dialogQty)}
                    </Button>
                  </>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Sell & My Shop dialogs */}
      <SellCardDialog open={showSell} onOpenChange={setShowSell} sellerFeePct={config.seller_fee_pct ?? 9.85} onCreated={fetchAll} />
      <MyShopDialog open={showMyShop} onOpenChange={setShowMyShop} onChanged={fetchAll} />

      {/* Cart dialog */}
      <Dialog open={showCart} onOpenChange={setShowCart}>
        <DialogContent data-testid="shop-cart-dialog" className="bg-slate-900 border-slate-700 w-[calc(100%-2rem)] max-w-sm max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg text-white">Your Cart</DialogTitle>
            <DialogDescription className="text-slate-500 text-xs">One order, one shipment — everything ships together.</DialogDescription>
          </DialogHeader>
          {cartEntries.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-6">Cart is empty</p>
          ) : (
            <div className="space-y-3">
              <div className="space-y-2">
                {cartEntries.map(([id, qty]) => {
                  const l = byId[id];
                  return (
                    <div key={id} data-testid={`cart-item-${id.slice(0, 8)}`} className="flex items-center gap-2.5 bg-slate-800/50 rounded-xl p-2.5">
                      {l.photo_urls?.[0]
                        ? <img src={l.photo_urls[0]} alt="" className="w-10 h-10 rounded-lg object-cover border border-slate-700 flex-shrink-0" />
                        : <div className="w-10 h-10 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center flex-shrink-0"><Tag className="w-4 h-4 text-slate-600" /></div>}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-white truncate">{l.title} {l.is_limited && <span className="text-amber-400">✨</span>}</p>
                        <p className="text-[10px] text-slate-500">{fmt(l.price_xrp)} each</p>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <button data-testid={`cart-minus-${id.slice(0, 8)}`} onClick={() => setQty(id, qty - 1)} className="w-6 h-6 rounded-md bg-slate-700 flex items-center justify-center text-white"><Minus className="w-3 h-3" /></button>
                        <span className="text-white text-xs font-bold w-5 text-center">{qty}</span>
                        <button data-testid={`cart-plus-${id.slice(0, 8)}`} onClick={() => setQty(id, qty + 1)} disabled={qty >= (l.quantity || 1)} className="w-6 h-6 rounded-md bg-slate-700 flex items-center justify-center text-white disabled:opacity-40"><Plus className="w-3 h-3" /></button>
                        <button data-testid={`cart-remove-${id.slice(0, 8)}`} onClick={() => setQty(id, 0)} className="w-6 h-6 rounded-md bg-red-900/40 flex items-center justify-center text-red-400 ml-1"><Trash2 className="w-3 h-3" /></button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {nextTier && regularQty > 0 && (
                <p data-testid="cart-discount-nudge" className="text-[11px] text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-lg px-2.5 py-1.5">
                  🔥 Add {nextTier.min_qty - regularQty} more bulk-eligible card{nextTier.min_qty - regularQty > 1 ? 's' : ''} to unlock {nextTier.pct}% off!
                </p>
              )}

              <div className="space-y-1.5">
                <p className="text-[10px] font-bold text-slate-400 uppercase">Shipping</p>
                <div className="grid grid-cols-2 gap-2">
                  {[['domestic', 'Domestic', config.shipping_domestic], ['international', 'International', config.shipping_international]].map(([zone, label, fee]) => (
                    <button
                      key={zone}
                      data-testid={`cart-shipping-${zone}`}
                      onClick={() => setShippingZone(zone)}
                      className={`rounded-xl border p-2.5 text-left transition-colors ${shippingZone === zone ? 'bg-cyan-500/15 border-cyan-500/50' : 'bg-slate-800/50 border-slate-700'}`}
                    >
                      <p className={`text-xs font-bold ${shippingZone === zone ? 'text-cyan-300' : 'text-white'}`}>{label}</p>
                      <p className="text-[10px] text-slate-400">{fmt(fee)} flat</p>
                    </button>
                  ))}
                </div>
              </div>

              <div className="bg-slate-800/50 rounded-xl p-3 space-y-1.5">
                <div className="flex justify-between text-xs"><span className="text-slate-400">Subtotal ({cartCount} cards)</span><span className="text-white font-semibold">{fmt(regularSubtotal + limitedSubtotal)}</span></div>
                {discount > 0 && (
                  <div className="flex justify-between text-xs" data-testid="cart-discount-line">
                    <span className="text-amber-300">Bulk discount ({discountPct}%)</span><span className="text-amber-300 font-semibold">−{fmt(discount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-xs"><span className="text-slate-400">Shipping ({shippingZone})</span><span className="text-white font-semibold">{fmt(shippingFee)}</span></div>
                <div className="flex justify-between text-sm border-t border-slate-700 pt-1.5"><span className="text-slate-300 font-bold">Total</span><span data-testid="cart-total" className="text-cyan-300 font-bold">{fmt(total)}</span></div>
                {isAuthenticated && (
                  <div className="flex justify-between text-[10px]"><span className="text-slate-500">Your balance</span><span className={(user?.balance || 0) >= total ? 'text-slate-400' : 'text-red-400'}>{fmt(user?.balance || 0)}</span></div>
                )}
                <p className="text-[10px] text-slate-500 pt-1">Funds held in escrow until you confirm delivery. Ships signature-required — without a signature on delivery, all sales are final.</p>
              </div>

              <Button
                data-testid="cart-checkout-button"
                onClick={checkout}
                disabled={busy || (isAuthenticated && (user?.balance || 0) < total)}
                className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 h-11 font-semibold"
              >
                {busy ? 'Placing order…' : `Checkout — ${fmt(total)}`}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
